/*
 * hp-product-recommendations -- the PDP's "You May Also Like" band.
 *
 * Fills the band to its limit from three sources, in priority order:
 *   1. complementary   (Search & Discovery, curated per product)
 *   2. related         (Shopify's automatic set)
 *   3. fallback        (the block's product list, already on the page)
 * skipping the product being viewed and anything already chosen, so any mix
 * works: one complementary product with a limit of 2 takes the second slot
 * from related, or from the fallback list when related is empty.
 *
 * The endpoint answers one intent per request, so both are fetched in
 * parallel through sections/hp-recommendations-render.liquid, which returns
 * just the cards (server-rendered by hp-product-card, each wrapped with its
 * product id). This script only chooses and orders them; it renders nothing.
 *
 * FAILS SAFE: the fallback list is server-rendered first, so without this
 * script (or if both fetches fail) the band shows the fallback exactly as
 * before. A failed or empty source simply contributes nothing. When the
 * merge comes to nothing at all, the band is hidden rather than left as a
 * heading over an empty list.
 *
 * Loads when the band's column is within 600px of the viewport, as base's
 * <product-recommendations> did; without IntersectionObserver it loads at once.
 */
(() => {
  if (customElements.get('hp-product-recommendations')) return;

  // The recommendations endpoint's own maximum.
  const MAX_LIMIT = 10;

  class HpProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      if (!('IntersectionObserver' in window)) {
        this.load();
        return;
      }
      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.load();
        },
        { rootMargin: '0px 0px 600px 0px' }
      );
      // WATCH THE BAND'S PARENT, NOT THE BAND. With no fallback list the band
      // starts `hidden` (display: none), and a display:none element never
      // reports as intersecting, so observing it meant the fetch never ran
      // and the band stayed empty on every such product. The parent (the
      // product info column) is always laid out.
      const band = this.closest('.hp-recs');
      this.observer.observe((band && band.parentElement) || this);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    async fetchCards(intent, limit) {
      const url = new URL(this.dataset.url, window.location.origin);
      url.searchParams.set('intent', intent);
      url.searchParams.set('limit', String(limit));
      const response = await fetch(url);
      if (!response.ok) throw new Error(`${intent}: ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      return [...doc.querySelectorAll('[data-hp-recs-cards] > [data-hp-rec-id]')];
    }

    async load() {
      if (this.started) return;
      this.started = true;

      const limit = Math.max(1, parseInt(this.dataset.limit, 10) || 2);
      // Related may repeat complementary picks, so ask it for extra to cover
      // the duplicates that get skipped.
      const [complementary, related] = await Promise.allSettled([
        this.fetchCards('complementary', Math.min(MAX_LIMIT, limit)),
        this.fetchCards('related', Math.min(MAX_LIMIT, limit * 2)),
      ]);
      const fallback = [...this.querySelectorAll(':scope > [data-hp-rec-id]')];

      const chosen = [];
      const seen = new Set([String(this.dataset.productId)]);
      const take = (nodes, source) => {
        for (const node of nodes) {
          if (chosen.length >= limit) return;
          const id = String(node.dataset.hpRecId);
          if (!id || seen.has(id)) continue;
          seen.add(id);
          const item = node.ownerDocument === document ? node : document.importNode(node, true);
          item.dataset.hpRecSource = source;
          item.hidden = false;
          chosen.push(item);
        }
      };
      take(complementary.status === 'fulfilled' ? complementary.value : [], 'complementary');
      take(related.status === 'fulfilled' ? related.value : [], 'related');
      take(fallback, 'fallback');

      [complementary, related].forEach((result) => {
        if (result.status === 'rejected') console.error('[hp-recs]', result.reason);
      });

      const band = this.closest('.hp-recs');
      if (!chosen.length) {
        if (band) band.hidden = true;
        return;
      }
      this.replaceChildren(...chosen);
      if (band) band.hidden = false;
    }
  }

  customElements.define('hp-product-recommendations', HpProductRecommendations);
})();
